import { useEffect, useRef } from 'react'
import { useBookDemo } from '../components/BookDemoContext'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import './ProductTour.css'

export default function ProductTour() {
  const openBookDemo = useBookDemo()
  const tourNavRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const sections = document.querySelectorAll('.tour-step')
    const navLinks = tourNavRef.current?.querySelectorAll('a[data-step]') || []

    const onScroll = () => {
      let current = ''
      sections.forEach(s => {
        if (window.scrollY >= (s as HTMLElement).offsetTop - 200) current = s.id
      })
      navLinks.forEach((a: Element) => {
        a.classList.toggle('active', (a as HTMLElement).dataset.step === current)
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollToStep = (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      const offset = 130
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
    }
  }

  return (
    <>
      <Navbar />

      {/* ── Tour Hero ── */}
      <section className="tour-hero">
        <div className="container">
          <h1>See PossoBuild in Action</h1>
          <p>A step-by-step walkthrough of how one conversational AI engine powers your entire talent lifecycle.</p>
          <p className="hero-sub">Every scenario below is drawn from real challenges our enterprise customers brought to us.</p>
          <button className="btn-primary" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
        </div>
      </section>

      {/* ── Sticky Tour Nav ── */}
      <nav className="tour-nav" ref={tourNavRef}>
        <a href="#step-hire"    data-step="step-hire"    onClick={e => scrollToStep(e, 'step-hire')}>👤 Step 1: Hire</a>
        <a href="#step-develop" data-step="step-develop" onClick={e => scrollToStep(e, 'step-develop')}>📈 Step 2: Develop</a>
        <a href="#step-measure" data-step="step-measure" onClick={e => scrollToStep(e, 'step-measure')}>✅ Step 3: Measure</a>
        <a href="#step-support" data-step="step-support" onClick={e => scrollToStep(e, 'step-support')}>💬 Step 4: Support</a>
      </nav>


      {/* ══════════════════════════════════════
          STEP 1 — SmartHire
      ══════════════════════════════════════ */}
      <section className="tour-step" id="step-hire">
        <div className="container">
          <div className="tour-step__inner">

            {/* Copy */}
            <div className="tour-content">
              <div className="step-badge" style={{background:'#1c2b3a'}}>
                <div className="step-num">1</div> Hire Talent
              </div>
              <h2>SmartHire</h2>
              <p className="tagline">"Conversational AI interviews that think like a senior interviewer."</p>
              <p>SmartHire doesn't ask scripted questions — it listens to each response and probes deeper, exactly like your best interviewer would. Every follow-up is generated dynamically based on what the candidate just said.</p>
              <p>Patented integrity detection adds interview fraud detection and AI interview cheating detection by flagging AI-assisted responses in real time.</p>
              <ul>
                <li>Adaptive follow-up questions based on each answer</li>
                <li>Scenario-based coding interviews that test real thinking</li>
                <li>Competency-based evaluation across technical depth, communication, and reasoning</li>
                <li>Patented integrity detection flags AI-assisted responses in real time</li>
                <li>Industry-agnostic — works for any role across any sector</li>
                <li>Generates structured hiring intelligence reports automatically</li>
              </ul>
              <p className="italic-note">SmartHire evaluates how candidates think, not just what answers they give. Patented integrity detection identifies AI-assisted responses — automatically.</p>
            </div>

            {/* Phone + Result Card */}
            <div className="phone-wrap">
              <div className="phone">
                <div className="phone-statusbar">
                  <span>9:41</span>
                  <span>●●● 5G 🔋</span>
                </div>
                <div className="chat-appbar" style={{background:'#1c2b3a'}}>
                  <div className="chat-avatar" style={{background:'#c9a84c', color:'#1c2b3a'}}>SH</div>
                  <div className="chat-appbar-info">
                    <span className="chat-appbar-name">SmartHire AI</span>
                    <span className="chat-appbar-status">● Live Interview — Software Engineer</span>
                  </div>
                </div>
                <div className="chat-messages">
                  <div className="chat-date">Today, Interview Session</div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#c9a84c', color:'#1c2b3a'}}>SH</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">Describe a REST API you designed recently. Walk me through your architecture decisions.</div>
                      <span className="msg-time msg-time--ai">9:41</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#1c2b3a'}}>I built a microservices API using Flask with Redis caching to handle high-volume requests efficiently.</div>
                      <span className="msg-time">9:42</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#c9a84c', color:'#1c2b3a'}}>SH</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">You mentioned Redis caching. How did you ensure cache consistency across services when data was updated?</div>
                      <span className="msg-time msg-time--ai">9:42</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#1c2b3a'}}>I used cache invalidation with TTL and event-driven updates via a message queue.</div>
                      <span className="msg-time">9:43</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#c9a84c', color:'#1c2b3a'}}>SH</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">Good. If traffic increased 10×, how would you scale this to prevent bottlenecks?</div>
                      <span className="msg-time msg-time--ai">9:43</span>
                    </div>
                  </div>

                  <div className="typing-indicator">
                    <div className="msg-avatar" style={{background:'#c9a84c', color:'#1c2b3a'}}>SH</div>
                    <div className="typing-bubble">
                      <div className="typing-dot"/><div className="typing-dot"/><div className="typing-dot"/>
                    </div>
                  </div>
                </div>
              </div>

              {/* Result Card */}
              <div className="result-card">
                <div className="result-header" style={{background:'linear-gradient(135deg,#1c2b3a,#2e4057)'}}>
                  <div>
                    <h4>Interview Intelligence Report</h4>
                    <p>Mid-Level Software Engineer · AI Generated</p>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div className="result-score-big">8.5</div>
                    <div style={{fontSize:'0.72rem', opacity:0.75}}>/ 10</div>
                  </div>
                </div>
                <div className="result-body">
                  <div className="result-row">
                    <span>API Architecture</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'90%', background:'linear-gradient(90deg,#c9a84c,#e0bc6e)'}}/></div>
                      <span className="result-val" style={{color:'#a8893a'}}>9.0</span>
                    </div>
                  </div>
                  <div className="result-row">
                    <span>System Design</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'85%', background:'linear-gradient(90deg,#c9a84c,#e0bc6e)'}}/></div>
                      <span className="result-val" style={{color:'#a8893a'}}>8.5</span>
                    </div>
                  </div>
                  <div className="result-row">
                    <span>Problem Solving</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'82%', background:'linear-gradient(90deg,#c9a84c,#e0bc6e)'}}/></div>
                      <span className="result-val" style={{color:'#a8893a'}}>8.2</span>
                    </div>
                  </div>
                  <div className="result-row">
                    <span>Communication</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'88%', background:'linear-gradient(90deg,#c9a84c,#e0bc6e)'}}/></div>
                      <span className="result-val" style={{color:'#a8893a'}}>8.8</span>
                    </div>
                  </div>
                  <div className="verdict-badge" style={{background:'#fef9ec', border:'1px solid #e0bc6e', color:'#7a5c1e'}}>
                    ✅ Overall: 8.5 / 10 &nbsp;·&nbsp; Recommendation: <strong>Strong Hire</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════
          STEP 2 — SmartDevelop
      ══════════════════════════════════════ */}
      <section className="tour-step tour-step--alt" id="step-develop">
        <div className="container">
          <div className="tour-step__inner tour-step__inner--reverse">

            {/* Copy */}
            <div className="tour-content">
              <div className="step-badge" style={{background:'#4a1d96'}}>
                <div className="step-num">2</div> Develop Talent
              </div>
              <h2>SmartDevelop</h2>
              <p className="tagline">"AI career readiness assessments that help employees prepare for their next role."</p>
              <p>Employees select the role they aspire to — the next step in their career. SmartDevelop runs the same adaptive conversational assessment as SmartHire, but instead of a hiring report, it produces a personalized development roadmap.</p>
              <ul>
                <li>Employee selects their target role (e.g. Senior Engineer, Team Lead)</li>
                <li>AI conducts conversational readiness assessment</li>
                <li>Platform identifies strengths and skill gaps for that specific role</li>
                <li>Personalized development roadmap generated automatically</li>
                <li>Clear, actionable path to the next stage of their career</li>
              </ul>
            </div>

            {/* Phone + Roadmap Card */}
            <div className="phone-wrap">
              <div className="phone">
                <div className="phone-statusbar">
                  <span>10:15</span>
                  <span>●●● 5G 🔋</span>
                </div>
                <div className="chat-appbar" style={{background:'#4a1d96'}}>
                  <div className="chat-avatar" style={{background:'#ddd6fe', color:'#4a1d96'}}>SD</div>
                  <div className="chat-appbar-info">
                    <span className="chat-appbar-name">SmartDevelop AI</span>
                    <span className="chat-appbar-status">● Career Readiness — Senior Engineer</span>
                  </div>
                </div>
                <div className="chat-messages">
                  <div className="chat-date">Career Assessment Session</div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#7c3aed', color:'#fff'}}>SD</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">You've selected Senior Software Engineer as your target role. Let's assess your readiness. Tell me about a time you led a complex technical initiative.</div>
                      <span className="msg-time msg-time--ai">10:15</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#4a1d96'}}>I led the migration of our monolith to microservices over 6 months, coordinating across 3 teams.</div>
                      <span className="msg-time">10:16</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#7c3aed', color:'#fff'}}>SD</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">That's significant scope. How did you handle disagreements between teams on the architecture direction?</div>
                      <span className="msg-time msg-time--ai">10:16</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#4a1d96'}}>I facilitated architecture review sessions and built alignment through data on performance trade-offs.</div>
                      <span className="msg-time">10:17</span>
                    </div>
                  </div>

                  <div className="typing-indicator">
                    <div className="msg-avatar" style={{background:'#7c3aed', color:'#fff'}}>SD</div>
                    <div className="typing-bubble">
                      <div className="typing-dot"/><div className="typing-dot"/><div className="typing-dot"/>
                    </div>
                  </div>
                </div>
              </div>

              {/* Roadmap Card */}
              <div className="result-card">
                <div className="result-header" style={{background:'linear-gradient(135deg,#4a1d96,#1c2b3a)'}}>
                  <div>
                    <h4>Career Readiness Assessment</h4>
                    <p>Target: Senior Software Engineer</p>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div className="result-score-big">72%</div>
                    <div style={{fontSize:'0.72rem', opacity:0.75}}>Readiness</div>
                  </div>
                </div>
                <div className="result-body">
                  <div style={{fontSize:'0.82rem', marginBottom:'0.75rem'}}>
                    <strong style={{color:'#4a1d96'}}>✅ Strengths:</strong>
                    <span style={{color:'#6b7280'}}> API design · Database architecture · Code quality</span>
                  </div>
                  <div style={{fontSize:'0.82rem', marginBottom:'1rem'}}>
                    <strong style={{color:'#b91c1c'}}>📌 Development Areas:</strong>
                    <span style={{color:'#6b7280'}}> Distributed systems · Performance optimization · Team leadership</span>
                  </div>
                  <div style={{fontSize:'0.82rem', fontWeight:700, color:'#0e1820', marginBottom:'0.6rem'}}>Personalized Development Plan</div>
                  <div className="plan-pills">
                    <div className="plan-pill">
                      <span className="plan-pct" style={{color:'#4a1d96'}}>70%</span>
                      <span>On-the-job assignments targeting distributed systems</span>
                    </div>
                    <div className="plan-pill">
                      <span className="plan-pct" style={{color:'#4a1d96'}}>20%</span>
                      <span>Learning from senior engineers and mentors</span>
                    </div>
                    <div className="plan-pill">
                      <span className="plan-pct" style={{color:'#4a1d96'}}>10%</span>
                      <span>Formal training programs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════
          STEP 3 — SmartAssess
      ══════════════════════════════════════ */}
      <section className="tour-step" id="step-measure">
        <div className="container">
          <div className="tour-step__inner">

            {/* Copy */}
            <div className="tour-content">
              <div className="step-badge" style={{background:'#0e7490'}}>
                <div className="step-num">3</div> Measure Talent
              </div>
              <h2>SmartAssess</h2>
              <p className="tagline">"Conversational competency assessments that measure what employees actually know."</p>
              <p>SmartAssess goes beyond training completion tick-boxes. It uses the same conversational AI engine to evaluate actual knowledge and competency — whether validating a training program or benchmarking skills on demand.</p>
              <ul>
                <li>Assess knowledge at any stage of training — mid-way or post-completion</li>
                <li>Evaluate workforce competencies across roles on demand</li>
                <li>Identify skill gaps at individual and team level</li>
                <li>Measure training ROI with real competency data</li>
                <li>Works for any domain — technical, sales, compliance, operations and more</li>
              </ul>
              <p className="italic-note">SmartAssess measures what employees actually know, not just what training they completed.</p>
            </div>

            {/* Phone + Results Card */}
            <div className="phone-wrap">
              <div className="phone">
                <div className="phone-statusbar">
                  <span>2:30 PM</span>
                  <span>●●● 5G 🔋</span>
                </div>
                <div className="chat-appbar" style={{background:'#0e4f5c'}}>
                  <div className="chat-avatar" style={{background:'#a5f3fc', color:'#0e4f5c'}}>SA</div>
                  <div className="chat-appbar-info">
                    <span className="chat-appbar-name">SmartAssess AI</span>
                    <span className="chat-appbar-status">● Competency Assessment — Cloud Engineering</span>
                  </div>
                </div>
                <div className="chat-messages">
                  <div className="chat-date">Post-Training Validation</div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#0e7490', color:'#fff'}}>SA</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">Explain how container orchestration works in Kubernetes and why it matters for enterprise deployments.</div>
                      <span className="msg-time msg-time--ai">2:30</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#0e7490'}}>Kubernetes manages containers using pods and services, ensuring availability and scaling across nodes.</div>
                      <span className="msg-time">2:31</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#0e7490', color:'#fff'}}>SA</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">Good start. How does Kubernetes ensure high availability when a node fails unexpectedly?</div>
                      <span className="msg-time msg-time--ai">2:31</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#0e7490'}}>It reschedules pods automatically to healthy nodes using the controller manager.</div>
                      <span className="msg-time">2:32</span>
                    </div>
                  </div>

                  <div className="typing-indicator">
                    <div className="msg-avatar" style={{background:'#0e7490', color:'#fff'}}>SA</div>
                    <div className="typing-bubble">
                      <div className="typing-dot"/><div className="typing-dot"/><div className="typing-dot"/>
                    </div>
                  </div>
                </div>
              </div>

              <div className="result-card">
                <div className="result-header" style={{background:'linear-gradient(135deg,#0e4f5c,#0e7490)'}}>
                  <div>
                    <h4>Competency Assessment Results</h4>
                    <p>Cloud Engineering · Post-Training Validation</p>
                  </div>
                </div>
                <div className="result-body">
                  <div className="result-row">
                    <span>Container Orchestration</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'88%', background:'linear-gradient(90deg,#0e7490,#22d3ee)'}}/></div>
                      <span className="result-val" style={{color:'#0e7490'}}>8.8</span>
                    </div>
                  </div>
                  <div className="result-row">
                    <span>High Availability Design</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'75%', background:'linear-gradient(90deg,#0e7490,#22d3ee)'}}/></div>
                      <span className="result-val" style={{color:'#0e7490'}}>7.5</span>
                    </div>
                  </div>
                  <div className="result-row">
                    <span>Resource Management</span>
                    <div className="result-bar-wrap">
                      <div className="result-bar"><div className="result-bar-fill" style={{width:'70%', background:'linear-gradient(90deg,#0e7490,#22d3ee)'}}/></div>
                      <span className="result-val" style={{color:'#0e7490'}}>7.0</span>
                    </div>
                  </div>
                  <div className="verdict-badge" style={{background:'#ecfeff', border:'1px solid #a5f3fc', color:'#0e4f5c'}}>
                    📋 Training Effectiveness: <strong>Strong</strong> &nbsp;·&nbsp; Skill Gap: Resource Management
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════
          STEP 4 — SmartAssist
      ══════════════════════════════════════ */}
      <section className="tour-step tour-step--alt" id="step-support">
        <div className="container">
          <div className="tour-step__inner tour-step__inner--reverse">

            {/* Copy */}
            <div className="tour-content">
              <div className="step-badge" style={{background:'#065f46'}}>
                <div className="step-num">4</div> Support Talent
              </div>
              <h2>SmartAssist</h2>
              <p className="tagline">"AI assistant for company knowledge."</p>
              <p>Employees can ask questions about company policies, procedures, and internal documentation in natural conversation. SmartAssist retrieves answers instantly with source document references — no more hunting through portals.</p>
              <ul>
                <li>Instant answers from company policies and internal knowledge portals</li>
                <li>Every answer cites its source document for complete transparency</li>
                <li>Dramatically reduces time spent searching through internal portals</li>
                <li>Reduces HR support tickets significantly</li>
                <li>Available 24/7 on any device</li>
              </ul>
              <p className="italic-note">Your entire company handbook — accessible through conversation.</p>
            </div>

            {/* Phone */}
            <div className="phone-wrap">
              <div className="phone">
                <div className="phone-statusbar">
                  <span>4:05 PM</span>
                  <span>●●● 5G 🔋</span>
                </div>
                <div className="chat-appbar" style={{background:'#064e3b'}}>
                  <div className="chat-avatar" style={{background:'#6ee7b7', color:'#064e3b'}}>AI</div>
                  <div className="chat-appbar-info">
                    <span className="chat-appbar-name">SmartAssist</span>
                    <span className="chat-appbar-status">● Employee Knowledge Assistant · Online</span>
                  </div>
                </div>
                <div className="chat-messages">
                  <div className="chat-date">Today</div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#065f46'}}>What is our travel reimbursement policy for client visits?</div>
                      <span className="msg-time">4:05</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#059669', color:'#fff'}}>AI</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">
                        Employees are eligible for reimbursement of approved business travel expenses — transport, accommodation, and meals for client visits. Claims must be submitted within 30 days with supporting receipts.
                        <div><span className="source-chip">📄 Corporate Travel Policy — §3.2</span></div>
                      </div>
                      <span className="msg-time msg-time--ai">4:05</span>
                    </div>
                  </div>

                  <div className="msg-row msg-row--user">
                    <div>
                      <div className="msg-bubble msg-bubble--user" style={{background:'#065f46'}}>How many annual leave days do I get as a full-time employee?</div>
                      <span className="msg-time">4:06</span>
                    </div>
                  </div>

                  <div className="msg-row">
                    <div className="msg-avatar" style={{background:'#059669', color:'#fff'}}>AI</div>
                    <div>
                      <div className="msg-bubble msg-bubble--ai">
                        Full-time employees receive 20 days of annual leave plus all public holidays. Unused leave may be carried forward for up to one year.
                        <div><span className="source-chip">📄 Employee Handbook — §4.1</span></div>
                      </div>
                      <span className="msg-time msg-time--ai">4:06</span>
                    </div>
                  </div>

                  <div className="typing-indicator">
                    <div className="msg-avatar" style={{background:'#059669', color:'#fff'}}>AI</div>
                    <div className="typing-bubble">
                      <div className="typing-dot"/><div className="typing-dot"/><div className="typing-dot"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ── Talent Intelligence Loop ── */}
      <section className="loop-section">
        <div className="container">
          <h2>The Talent Intelligence Loop</h2>
          <p>All four products run on one conversational AI engine — creating continuous, connected intelligence across every stage of the employee lifecycle.</p>
          <div className="loop-diagram">
            <div className="loop-step">
              <div className="loop-icon">👤</div>
              <h4>Hire</h4>
              <p>SmartHire</p>
            </div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">
              <div className="loop-icon">📈</div>
              <h4>Develop</h4>
              <p>SmartDevelop</p>
            </div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">
              <div className="loop-icon">✅</div>
              <h4>Measure</h4>
              <p>SmartAssess</p>
            </div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">
              <div className="loop-icon">💬</div>
              <h4>Support</h4>
              <p>SmartAssist</p>
            </div>
          </div>
          <div className="loop-cta">
            <button className="btn-primary" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
            <a href="/how-it-works" className="btn-secondary">See How It Works →</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
